/**
 * Which sign-in link this browser came in through.
 *
 * A prospect is sent one link — /login/pharma — and their session is pinned to
 * that company. Three places used to send everyone to the bare /login: signing
 * out, a session expiring (the 401 interceptor), and AuthGuard bouncing a
 * signed-out visitor. On a multi-company install that page is a plain form with
 * no company on it, so the prospect would land somewhere that no longer said
 * whose workspace it was, and the one-click demo logins would be gone.
 *
 * So the key is remembered here, deliberately OUTSIDE the auth store: the
 * store is wiped on sign-out, and sign-out is exactly when this is needed.
 * Signing in through the bare /login clears it, because that is the person
 * choosing not to use a link.
 */

const STORAGE_KEY = 'erp_login_key';

/** Keys are URL slugs: lowercase letters, digits and hyphens. */
const VALID = /^[a-z0-9][a-z0-9-]{0,79}$/;

export function rememberLoginKey(key: string | null | undefined): void {
  try {
    if (key && VALID.test(key)) {
      window.localStorage.setItem(STORAGE_KEY, key);
    } else {
      window.localStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    /* Storage blocked (private mode, quota) — fall back to the bare /login. */
  }
}

export function rememberedLoginKey(): string | null {
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    return v && VALID.test(v) ? v : null;
  } catch {
    return null;
  }
}

/** Where to send someone who needs to sign in again. */
export function loginPath(): string {
  const key = rememberedLoginKey();
  return key ? `/login/${key}` : '/login';
}

/** True for /login and /login/:key — used to avoid redirect loops. */
export function isLoginPath(pathname: string): boolean {
  return pathname === '/login' || pathname.startsWith('/login/');
}
